import { toJS, makeAutoObservable } from 'mobx';
import {DomainStore} from 'shared/store';
import {endpoints, types} from 'main/tools';
import Employee from 'main/stores/employees/Employee';
import {PerformanceReviewReview} from 'main/stores/performance_reviews';
import _ from 'lodash';
import {withDeleted} from 'shared/tools';

class SelectReviewersState {
  onHide;
  onSave;
  cycle;
  employees;
  store = new DomainStore();

  isOpen = false;
  reviewee;
  selectedManagers = [];
  selectedPeers = [];
  errors = {};

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  receiveProps({isOpen, onHide, reviewee, cycle, onSave}) {
    this.isOpen = isOpen;
    this.onHide = onHide;
    this.onSave = onSave;
    this.reviewee = reviewee;
    this.cycle = cycle;
  }

  async load() {
    await this.store._compose([
      endpoints.EMPLOYEES.ALL
    ]);

    this.employees = _.reject(
      this.store.getAllOrdered(types.EMPLOYEE, Employee),
      employee => employee.equals(this.reviewee.employee)
    );

    this.selectedManagers = toJS(this.reviewee.reviewManagerEmployees);

    if (this.cycle.peerType) {
      this.selectedPeers = _.chain(this.reviewee.reviews)
        .filter(r => r.type.equals(this.cycle.peerType))
        .map(r => _.find(this.employeesWithReviewers, {id: r.reviewer.id}))
        .value();
    } else {
      this.selectedPeers = [];
    }
  }

  addPeer(peer) {
    this.selectedPeers.push(peer);
  }

  removePeer(peer) {
    this.selectedPeers.remove(peer);
  }

  addManagerReviewer(manager) {
    this.selectedManagers.push(manager);
  }

  removeManagerReviewer(manager) {
    _.remove(this.selectedManagers, {id: manager.id});    
  }

  updateManagerReviewer(manager) {
    this.selectedManagers = manager ? [manager] : [];
  }

  async saveReviewers() {
    const reviewee = this.reviewee.pick();

    reviewee.merge({reviewManagerEmployees: this.selectedManagers});

    if (this.cycle.managerType && !_.isEmpty(reviewee.reviewManagerEmployees)) {
      for (const manager of this.selectedManagers) {
        const existingManagerReview = _.find(this.currentManagerReviews, r => r.reviewer.id === manager.id);
        const managerReview = existingManagerReview ||
          new PerformanceReviewReview({reviewer: manager, type: this.cycle.managerType});

        reviewee.reviews.push(managerReview);
      }
    }

    if (this.cycle.peerType) {
      for (const peer of this.selectedPeers) {
        const existingPeerReview = _.find(this.currentPeerReviews, r => r.reviewer.id === peer.id);
        const peerReview = existingPeerReview ||
          new PerformanceReviewReview({reviewer: peer, type: this.cycle.peerType});

        reviewee.reviews.push(peerReview);
      }
    }

    const {model, errors} = await this.store.patch(reviewee);
    this.errors = errors;
    await this.__updateCycle();

    if (model) {
      this.onSave(model);
    }
  }

  get currentManagerReview() {
    return _.find(this.reviewee.reviews, r => r.type.equals(this.cycle.managerType));
  }

  get employeesWithReviewers() {
    return withDeleted(this.employees, this.reviewee.reviews.map(review => review.reviewer));
  }

  get currentPeerReviews() {
    return _.filter(this.reviewee.reviews, r => r.type.equals(this.cycle.peerType));
  }

  get currentManagerReviews() {
    return _.filter(this.reviewee.reviews, r => r.type.equals(this.cycle.managerType));
  }

  get showManagerSelector() {
    return !!this.cycle.managerType;
  }

  get showPeerSelector() {
    return !!this.cycle.peerType;
  }

  async __updateCycle() {
    await this.store._compose([
      endpoints.PERFORMANCE_REVIEWS.CYCLE.with(this.cycle.id)
    ]);

    const newCycle = this.store._getSingle(types.PERFORMANCE_REVIEWS.CYCLE);
    this.cycle.reviewersCount = newCycle.reviewersCount;
  }
}

export default SelectReviewersState;
